import type { ReactElement } from 'react'
import type { EntityId } from '@terros/common'
import { useProfile } from '@front/model'
import { PermissionTooltip, TButton } from '@components'
import type { DashboardScopeTarget } from './types'
import { DashboardScopePicker } from './DashboardScopePicker'

type Props = {
  canReorder: boolean
  isReordering: boolean
  /** Creating skips the picker for self-only viewers, so the button needs its own loading. */
  isCreating?: boolean
  /** What the `dashboard:manage` check runs against for the scope in view. */
  permissionTargetId?: EntityId
  selectedTarget?: DashboardScopeTarget
  onNewDashboard: () => void
  onSelectTarget: (target?: DashboardScopeTarget) => void
  onStopReordering: () => void
  startReordering: () => void
}

export function DashboardListToolbar({
  canReorder,
  isReordering,
  isCreating,
  permissionTargetId,
  selectedTarget,
  onNewDashboard,
  onSelectTarget,
  onStopReordering,
  startReordering,
}: Props): ReactElement {
  const { company, user } = useProfile()

  if (isReordering) {
    return (
      <TButton variant='secondary' onClick={onStopReordering}>
        Done
      </TButton>
    )
  }

  return (
    <>
      {canReorder && (
        <TButton icon='swap_vert' variant='secondary' onClick={startReordering}>
          Reorder
        </TButton>
      )}
      <DashboardScopePicker
        companyId={company?.companyId}
        companyName={company?.name}
        selectedTarget={selectedTarget}
        user={user}
        onSelect={onSelectTarget}
      />
      <PermissionTooltip permissionType='dashboard:manage' targetId={permissionTargetId}>
        <TButton icon='add' loading={isCreating} onClick={onNewDashboard}>
          New Dashboard
        </TButton>
      </PermissionTooltip>
    </>
  )
}
